import type { ReactNode } from 'react';

import { periodCountLabel } from './workshops-calendar-period';
import {
  calendarVisibility,
  type CalendarVisibility,
  type CalendarVisibilityInput,
} from './workshops-calendar-visibility';
import { WorkshopsCalendarCountLine } from './WorkshopsCalendarCountLine';
import type { WorkshopChip } from './workshopsCalendarTypes';

export interface UseCalendarVisibilityInput extends CalendarVisibilityInput {
  countLineFor?: (inPeriod: WorkshopChip[]) => string;
  showCancelled: boolean;
  onToggleCancelled: () => void;
}

/**
 * What the calendar shows and the header's count line to go with it, including
 * the show-cancelled switch the page holds. The toggle only joins the count
 * line when there is something to reveal, so a List view with no count and no
 * cancellations keeps an empty header line.
 */
export function useCalendarVisibility({
  countLineFor,
  showCancelled,
  onToggleCancelled,
  ...input
}: UseCalendarVisibilityInput): CalendarVisibility & { countLine: ReactNode } {
  const cal = calendarVisibility({ ...input, showCancelled });
  const periodCount = countLineFor
    ? countLineFor(cal.inPeriod)
    : periodCountLabel(input.view, input.anchor, input.weekStartsOn, cal.shown, input.now);

  const countLine =
    cal.cancelledInPeriod > 0 || showCancelled ? (
      <WorkshopsCalendarCountLine
        periodCount={periodCount}
        showCancelled={showCancelled}
        cancelledCount={cal.cancelledInPeriod}
        onToggleCancelled={onToggleCancelled}
      />
    ) : (
      periodCount
    );
  return { ...cal, countLine };
}
